import { Component, ChangeDetectionStrategy, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

import { PageTabsComponent, PageTab } from 'src/app/core/components/page-tabs/page-tabs.component';
import {
  PolicyGapAnalysisService,
  PolicyDocument,
  Finding,
  AggregateReport,
  ControlDomain,
} from './services/policy-gap-analysis.service';

import { RepositoryHubComponent } from './components/repository-hub/repository-hub.component';
import { DocumentDetailComponent } from './components/document-detail/document-detail.component';
import { AggregateViewComponent } from './components/aggregate-view/aggregate-view.component';
import { AnalysisDrilldownComponent } from './components/analysis-drilldown/analysis-drilldown.component';

@Component({
  selector: 'app-policy-gap-analysis',
  standalone: true,
  imports: [
    CommonModule,
    PageTabsComponent,
    RepositoryHubComponent,
    DocumentDetailComponent,
    AggregateViewComponent,
    AnalysisDrilldownComponent,
  ],
  templateUrl: './policy-gap-analysis.component.html',
  styleUrl: './policy-gap-analysis.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PolicyGapAnalysisComponent implements OnInit {
  private service = inject(PolicyGapAnalysisService);

  // Tab state
  public tabs: PageTab[] = [
    { id: 'repository', label: 'Document Repository' },
    { id: 'aggregate', label: 'Aggregate View' },
    { id: 'drilldown', label: 'Analysis Drilldown' },
  ];
  public activeTab = signal<string>('repository');

  // Data state
  public documents = signal<PolicyDocument[]>([]);
  public allFindings = signal<Finding[]>([]);
  public aggregateReport = signal<AggregateReport | null>(null);
  public isLoading = signal<boolean>(true);

  // Selection state
  public selectedDocument = signal<PolicyDocument | null>(null);
  public showDocumentDetail = signal<boolean>(false);
  public drilldownFilterDomain = signal<ControlDomain | null>(null);
  public drilldownFilterDocumentId = signal<number | null>(null);

  ngOnInit(): void {
    this.loadData();
  }

  public onTabChange(tabId: string): void {
    this.activeTab.set(tabId);
  }

  public onDocumentSelected(doc: PolicyDocument): void {
    this.selectedDocument.set(doc);
    this.showDocumentDetail.set(true);
  }

  public onCloseDocumentDetail(): void {
    this.showDocumentDetail.set(false);
  }

  public onViewAggregate(): void {
    this.activeTab.set('aggregate');
  }

  public onViewDrilldown(filters?: { domain?: ControlDomain; documentId?: number }): void {
    this.showDocumentDetail.set(false);
    this.drilldownFilterDomain.set(filters?.domain ?? null);
    this.drilldownFilterDocumentId.set(filters?.documentId ?? null);
    this.activeTab.set('drilldown');
  }

  public onDocumentUploaded(doc: PolicyDocument): void {
    this.documents.update((docs) => [doc, ...docs]);
  }

  public onBackToRepository(): void {
    this.showDocumentDetail.set(false);
    this.selectedDocument.set(null);
  }

  private loadData(): void {
    this.isLoading.set(true);

    this.service.getDocuments().subscribe({
      next: (docs) => this.documents.set(docs),
      error: (err) => console.error('Failed to load documents:', err),
    });

    this.service.getAllFindings().subscribe({
      next: (findings) => this.allFindings.set(findings),
      error: (err) => console.error('Failed to load findings:', err),
    });

    this.service.getAggregateReport().subscribe({
      next: (report) => {
        this.aggregateReport.set(report);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load aggregate report:', err);
        this.isLoading.set(false);
      },
    });
  }
}
